import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { requireAuth } from "utilities/auth";
import { useDisputifierMerchants } from "hooks/crm/useDisputifierMerchants";
import { SearchProvider, useSearch } from "context/SearchContext";
import SearchPanel from "components/SearchPanel";
import PageHeader from "components/PageLayout";
import PageKpis from "components/PageKpis";
import Skeleton from "components/Skeleton";
import {
  Shield,
  CheckCircle,
  AlertCircle,
  XCircle,
  Filter,
  CreditCard,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const PROGRAMS = ["rdr", "cdrn", "ethoca", "tc40"];

function programCount(merchant, program, status) {
  return (merchant.mids || []).filter(
    (m) => m.programs?.[program]?.status?.toLowerCase() === status
  ).length;
}

function DisputifierPortfolioInner() {
  const { filters } = useSearch();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const { merchants, pagination, loading } = useDisputifierMerchants({
    filters,
    page,
    limit: 25,
  });

  // Reset page when filters change
  const prevFiltersRef = React.useRef(filters);
  React.useEffect(() => {
    if (prevFiltersRef.current !== filters) {
      setPage(1);
      prevFiltersRef.current = filters;
    }
  }, [filters]);

  const hasActiveFilters = Object.values(filters).some((v) => v !== "" && v !== undefined);

  const totalMids = merchants.reduce((sum, m) => sum + (m.mids?.length || 0), 0);
  const activeMids = merchants.reduce(
    (sum, m) => sum + (m.mids?.filter((mid) => mid.status === "active").length || 0),
    0
  );

  return (
    <div>
      <PageHeader
        title="Disputifier"
        subtitle="Chargeback prevention portfolio across all companies."
        meta={[
          { label: "Merchants", value: (pagination.total || 0).toLocaleString() },
          { label: "Active MIDs", value: activeMids },
        ]}
        actions={
          <button
            className="btn btn-secondary"
            onClick={() => setFiltersOpen(true)}
          >
            <Filter className="h-4 w-4" />
            Filters
            {hasActiveFilters && (
              <span className="badge badge-brand ml-1">
                {Object.values(filters).filter((v) => v !== "" && v !== undefined).length}
              </span>
            )}
          </button>
        }
      />

      <div className="container py-6 space-y-6">
        {/* KPIs */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="surface rounded-xl ring-1 ring-border p-5">
                <Skeleton.Rect className="h-5 w-20" />
                <Skeleton.Rect className="h-3 w-12 mt-2" />
              </div>
            ))}
          </div>
        ) : (
          <PageKpis
            items={[
              {
                label: "Merchants",
                value: pagination.total || 0,
                icon: <Shield />,
                iconColor: "text-brand",
                iconBg: "bg-brand/20",
              },
              {
                label: "Total MIDs",
                value: totalMids,
                icon: <CreditCard />,
                iconColor: "text-blue-500",
                iconBg: "bg-blue-100",
              },
              {
                label: "Active MIDs",
                value: activeMids,
                icon: <CheckCircle />,
                iconColor: "text-green-500",
                iconBg: "bg-green-100",
              },
              {
                label: "Page",
                value: `${pagination.page} of ${pagination.totalPages}`,
                icon: <AlertCircle />,
                iconColor: "text-muted-foreground",
                iconBg: "bg-muted",
              },
            ]}
          />
        )}

        {/* Results count */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {merchants.length} of {pagination.total} merchants
          </p>
        </div>

        {/* Table */}
        {loading ? (
          <div className="space-y-3">
            {[0, 1, 2].map((i) => (
              <Skeleton.Rect key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : merchants.length > 0 ? (
          <div className="overflow-x-auto surface rounded-xl ring-1 ring-border">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  {["DBA Name", "Company", "MIDs", "RDR", "CDRN", "Ethoca", "TC40", "Enrolled"].map(
                    (col) => (
                      <th
                        key={col}
                        className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
                      >
                        {col}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {merchants.map((merchant) => (
                  <tr
                    key={merchant.dbaId}
                    className="hover:bg-muted/40 cursor-pointer transition-colors"
                    onClick={() => navigate(`/companies/${merchant.companyId}?tab=disputifier`)}
                  >
                    <td className="px-4 py-3 text-sm font-medium text-foreground">
                      {merchant.dbaName}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {merchant.companyId}
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground">
                      {merchant.mids?.length || 0}
                    </td>
                    {PROGRAMS.map((p) => {
                      const live = programCount(merchant, p, "live");
                      const inProgress =
                        programCount(merchant, p, "in progress") +
                        programCount(merchant, p, "submitted");
                      return (
                        <td key={p} className="px-4 py-3 text-sm">
                          {live > 0 ? (
                            <span className="inline-flex items-center gap-1 text-success">
                              <CheckCircle className="h-3.5 w-3.5" /> {live}
                            </span>
                          ) : inProgress > 0 ? (
                            <span className="inline-flex items-center gap-1 text-warning">
                              <AlertCircle className="h-3.5 w-3.5" /> {inProgress}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">
                              <XCircle className="h-3.5 w-3.5 inline" />
                            </span>
                          )}
                        </td>
                      );
                    })}
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {merchant.createdAt
                        ? new Date(merchant.createdAt).toLocaleDateString()
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="surface rounded-xl ring-1 ring-border p-12 text-center">
            <Shield className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">
              {hasActiveFilters ? "No merchants match your filters" : "No Merchants Enrolled"}
            </h3>
            <p className="text-sm text-muted-foreground">
              {hasActiveFilters
                ? "Try adjusting your filters."
                : "Enroll merchants from the Company Detail page or MID Detail page."}
            </p>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="btn btn-ghost h-9 w-9 p-0 disabled:opacity-40"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm text-muted-foreground px-3">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
              disabled={page >= pagination.totalPages}
              className="btn btn-ghost h-9 w-9 p-0 disabled:opacity-40"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      <SearchPanel
        namespace="disputifier"
        title="Filter Merchants"
        isExpanded={filtersOpen}
        onClose={() => setFiltersOpen(false)}
      />
    </div>
  );
}

function DisputifierPortfolio() {
  return (
    <SearchProvider namespace="disputifier">
      <DisputifierPortfolioInner />
    </SearchProvider>
  );
}

export default requireAuth(DisputifierPortfolio);
